/* ===========================================================================
  Bossi | Bike page behaviours on Horizon
  Markup: blocks/bossi-build-options.liquid, bossi-build-send.liquid, bossi-pathways.liquid,
  bossi-sticky-bar.liquid. Data: Content > Metaobjects > Build options in Shopify admin.

  - Build options: shows each question only when its build kit, bike and answer rules allow;
    shows upgrade prices; the bike's price becomes the build total while upgrades are chosen.
  - Add to Cart: checks required answers, then adds the bike (answers as line properties) and
    each paid upgrade nested under it (parent_id), and tells Horizon's cart so the drawer opens
    with the whole build in it.
  - Send this build: checks the build and attaches it, with the price-hold date, to the enquiry.
  - Pathway cards: Frameset or Core Build chooses the matching Build Kit buttons.
  - Phone bar: appears once the buy area has scrolled away; shows the current price or total.
============================================================================ */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/* ---------- Money, in the customer's currency ---------- */

// Liquid renders 123456 in the customer's currency (for example "$1,234.56" or "€1.234,56");
// the separators and symbol are copied from that sample.
function makeMoney(sample) {
  const match = typeof sample === 'string' ? sample.match(/1([^\d]?)234([^\d])56/) : null;
  if (!match) return (cents) => (cents / 100).toFixed(2);
  const [whole, thousands, decimal] = match;
  const at = sample.indexOf(whole);
  const before = sample.slice(0, at);
  const after = sample.slice(at + whole.length);
  return (cents) => {
    const value = Math.abs(Math.round(Number(cents) || 0));
    const units = String(Math.floor(value / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const minor = String(value % 100).padStart(2, '0');
    return (cents < 0 ? '-' : '') + before + units + decimal + minor + after;
  };
}

// For the enquiry email: no invisible spacing characters, and the currency code only once.
function plain(text, currency) {
  const t = String(text).replace(/[​-‍﻿]/g, '').trim();
  return currency && !t.includes(currency) ? `${t} ${currency}` : t;
}

function heldUntil(days) {
  const date = new Date(Date.now() + days * 86400000);
  try {
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch (e) {
    return date.toDateString();
  }
}

// Matches the current site's rule: part of the size and build kit title, ignoring capitals.
function contains(title, list) {
  const t = String(title || '').toLowerCase();
  return (list || []).some((k) => k && t.includes(String(k).toLowerCase()));
}

const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const routes = { add: `${root}cart/add.js`, cart: `${root}cart.js` };

function readConfig(el, selector) {
  try {
    return JSON.parse(el.querySelector(selector).textContent);
  } catch (e) {
    console.error('Bossi: could not read the settings.', e);
    return null;
  }
}

function sectionOf(el) {
  return el.closest('.shopify-section') || document.body;
}

// Horizon updates the size and build kit picker without a page load and announces it.
function onVariantChange(section, callback) {
  document.addEventListener(StandardEvents.productSelect, (event) => {
    if (!(event.target instanceof Node) || !section.contains(event.target)) return;
    const done = (resolved) => callback(resolved?.detail?.resource?.id || null);
    if (event.promise) event.promise.then(done).catch(() => {});
    else done(null);
  });
}

/* ---------- Build options ---------- */

function initBuild(box) {
  if (box.dataset.bossiReady) return;
  box.dataset.bossiReady = '1';

  const cfg = readConfig(box, '[data-bossi-build-config]');
  if (!cfg) return;
  const section = sectionOf(box);
  const form = section.querySelector('product-form-component form');
  if (!form) return;

  const money = makeMoney(cfg.moneySample);
  const priceMoney = cfg.showCurrency ? makeMoney(cfg.moneyCurrencySample) : money;
  const holdDays = cfg.holdDays || 30;
  const questions = cfg.questions || [];
  const byId = {};
  let variantId = null;

  questions.forEach((q) => {
    byId[q.id] = q;
    q.box = box.querySelector(`[data-q="${q.id}"]`);
    q.select = q.box.querySelector('select');
    q.error = q.box.querySelector('[data-q-error]');
    q.adds = q.box.querySelector('[data-q-adds]');
  });

  // The selected size and build kit, read from the picker itself, so the right variant is used
  // even if Horizon's own update has not arrived (a slow or failed request).
  const fromPicker = () => {
    const checked = [...section.querySelectorAll('input[type="radio"][data-option-name]:checked')].sort(
      (a, b) => Number(a.dataset.fieldsetIndex || 0) - Number(b.dataset.fieldsetIndex || 0)
    );
    if (!checked.length) return null;
    const title = checked.map((input) => input.value).join(' / ').toLowerCase();
    return cfg.variants.find((v) => String(v.title).toLowerCase() === title) || null;
  };
  const variant = () => {
    const picked = fromPicker();
    if (picked) return picked;
    const input = form.querySelector('[name="id"]');
    const id = Number(variantId || (input && input.value));
    return cfg.variants.find((v) => v.id === id) || cfg.variants[0];
  };

  const choiceOf = (q) => (q.select.value ? q.choices.find((c) => c.id === q.select.value) || null : null);

  function visibility() {
    const title = (variant() || {}).title || '';
    const memo = {};
    const visible = (q, depth) => {
      if (memo[q.id] !== undefined) return memo[q.id];
      let ok =
        (!q.showFor || !q.showFor.length || contains(title, q.showFor)) &&
        !contains(title, q.hideFor) &&
        !(q.hideForProducts || []).includes(cfg.productId);
      if (ok && q.whenQ) {
        const source = byId[q.whenQ];
        const answer = source && depth < 10 && visible(source, depth + 1) ? choiceOf(source) : null;
        ok = !!answer && (!q.whenA || !q.whenA.length || q.whenA.includes(answer.id));
      }
      memo[q.id] = ok;
      return ok;
    };
    questions.forEach((q) => visible(q, 0));
    return memo;
  }

  // Only questions that are showing count: hidden answers are never sent.
  function collect() {
    const shown = visibility();
    const result = { shown, props: {}, upgrades: [], missing: [], lines: [] };
    questions.forEach((q) => {
      if (!shown[q.id]) return;
      const choice = choiceOf(q);
      if (!choice) {
        if (q.required) result.missing.push(q);
        return;
      }
      const base = q.cartLabel || q.label;
      let key = base;
      for (let n = 2; Object.prototype.hasOwnProperty.call(result.props, key); n++) key = `${base} (${n})`;
      result.props[key] = choice.label;
      result.lines.push({ label: key, value: choice.label });
      if (choice.upgrade) result.upgrades.push(choice.upgrade);
    });
    return result;
  }

  const total = (result) => (variant()?.price || 0) + result.upgrades.reduce((sum, u) => sum + u.price, 0);

  // The bike's price shows the build total while upgrades are chosen. Horizon rewrites the
  // price on every size or build kit change, so it is re-applied then.
  const priceHost = section.querySelector(`product-price[data-product-id="${cfg.productId}"]`);
  let writing = false;
  function applyPrice(result) {
    if (!priceHost) return;
    const el = priceHost.querySelector('.price-item__group.price bdi') || priceHost.querySelector('bdi');
    if (!el) return;
    // Always the selected variant's price, or the build total while upgrades are chosen.
    const text = priceMoney(result.upgrades.length ? total(result) : variant()?.price || 0);
    if (el.textContent.trim() === text) return;
    writing = true;
    el.textContent = text;
    writing = false;
  }
  if (priceHost) {
    new MutationObserver(() => {
      if (!writing) applyPrice(collect());
    }).observe(priceHost, { childList: true, subtree: true, characterData: true });
  }

  const summary = box.querySelector('[data-bossi-summary]');
  const linesEl = box.querySelector('[data-bossi-lines]');
  const totalEl = box.querySelector('[data-bossi-total]');

  function render() {
    const result = collect();
    questions.forEach((q) => {
      const show = !!result.shown[q.id];
      q.box.hidden = !show;
      const chosen = choiceOf(q);
      if (!show || chosen) {
        q.box.classList.remove('is-invalid');
        q.error.hidden = true;
        q.select.removeAttribute('aria-invalid');
      }
      // Long choice names are cut off in the closed dropdown, so the cost is repeated here.
      if (q.adds) {
        q.adds.hidden = !(show && chosen && chosen.upgrade);
        q.adds.textContent = q.adds.hidden ? '' : `Adds ${money(chosen.upgrade.price)}`;
      }
    });
    linesEl.textContent = '';
    result.upgrades.forEach((u) => {
      const line = document.createElement('p');
      line.className = 'bossi-build__line';
      const name = document.createElement('span');
      name.textContent = `${u.title}: ${u.variant}`;
      const price = document.createElement('span');
      price.textContent = `+ ${money(u.price)}`;
      line.append(name, price);
      linesEl.append(line);
    });
    totalEl.textContent = money(total(result));
    summary.hidden = result.upgrades.length === 0;
    applyPrice(result);
    return result;
  }

  function flagMissing(missing) {
    missing.forEach((q) => {
      q.box.classList.add('is-invalid');
      q.error.hidden = false;
      q.select.setAttribute('aria-invalid', 'true');
    });
    if (!missing.length) return;
    missing[0].box.scrollIntoView({ behavior: 'smooth', block: 'center' });
    try {
      missing[0].select.focus({ preventScroll: true });
    } catch (e) {
      missing[0].select.focus();
    }
  }

  let cartError = null;
  function showCartError(message) {
    if (!cartError) {
      cartError = document.createElement('p');
      cartError.className = 'bossi-build-cart-error';
      cartError.setAttribute('role', 'alert');
      form.append(cartError);
    }
    cartError.textContent = message || '';
    cartError.hidden = !message;
  }

  const buttons = () => [...form.querySelectorAll('button[type="submit"], button[name="add"]')];
  const busy = (on) => buttons().forEach((b) => b.toggleAttribute('disabled', on));

  async function fetchCart() {
    const response = await fetch(routes.cart, { headers: { Accept: 'application/json' } });
    return response.json();
  }

  function cartForEvent(ajaxCart) {
    const cart = CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart);
    // The cart count shows bikes and parts, not the upgrades nested under a bike.
    if (cart && Array.isArray(ajaxCart.items)) {
      cart.totalQuantity = ajaxCart.items
        .filter((item) => !item.parent_relationship)
        .reduce((sum, item) => sum + item.quantity, 0);
    }
    return cart;
  }

  async function addToCart(quantity) {
    const result = render();
    if (result.missing.length) {
      flagMissing(result.missing);
      return;
    }
    const v = variant();
    const items = [{ id: v.id, quantity, properties: result.props }];
    result.upgrades.forEach((u) => items.push({ id: u.id, quantity, parent_id: v.id }));
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    showCartError('');
    busy(true);
    const deferred = CartLinesUpdateEvent.createPromise();
    form.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(routes.add, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const body = await response.text();
      let data = null;
      try {
        data = JSON.parse(body);
      } catch (e) {
        /* not JSON, for example a verification page */
      }
      if (!response.ok || !data || data.status) {
        throw new Error(
          (data && (data.description || data.message)) ||
            'This build could not be added to the cart. Please try again in a moment.'
        );
      }
      const ajaxCart = await fetchCart();
      deferred.resolve({
        cart: cartForEvent(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'bossi-build',
          sections: data.sections,
          itemCount: quantity,
          productId: String(cfg.productId),
          didError: false,
        },
      });
    } catch (error) {
      showCartError(error.message);
      try {
        const ajaxCart = await fetchCart();
        deferred.resolve({ cart: cartForEvent(ajaxCart), detail: { didError: true, source: 'bossi-build' } });
      } catch (e) {
        deferred.reject(error);
      }
    } finally {
      busy(false);
    }
  }

  // Horizon's own Add to Cart would post the bike without its upgrades, so it is taken over here.
  window.addEventListener(
    'click',
    (event) => {
      const button = event.target instanceof Element ? event.target.closest('button') : null;
      if (!button || !form.contains(button) || button.type !== 'submit') return;
      const result = render();
      if (result.missing.length) {
        event.preventDefault();
        event.stopImmediatePropagation();
        flagMissing(result.missing);
      }
    },
    true
  );
  window.addEventListener(
    'submit',
    (event) => {
      if (event.target !== form) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const qty = Math.max(1, parseInt(form.querySelector('[name="quantity"]')?.value, 10) || 1);
      addToCart(qty);
    },
    true
  );

  box.addEventListener('change', (event) => {
    if (event.target.matches('[data-q-select]')) render();
  });
  section.addEventListener('change', (event) => {
    if (event.target instanceof HTMLInputElement && event.target.dataset.optionName) setTimeout(render, 0);
  });
  onVariantChange(section, (id) => {
    if (id) variantId = id;
    setTimeout(render, 0);
  });
  render();

  const sendBox = section.querySelector('[data-bossi-send]');
  if (sendBox) initSend(sendBox, { cfg, money, render, collect, flagMissing, variant, total, holdDays });
}

/* ---------- Send this build ---------- */

function initSend(box, build) {
  if (box.dataset.bossiReady) return;
  box.dataset.bossiReady = '1';
  const { cfg, money, render, flagMissing, variant, total, holdDays } = build;
  const open = box.querySelector('[data-bossi-send-open]');
  const panel = box.querySelector('[data-bossi-send-panel]');
  const sendForm = panel.querySelector('form');
  const model = String(cfg.product || '').split(' - ')[0];
  const fills = [...box.querySelectorAll('[data-bossi-fill]')];
  fills.forEach((el) => el.setAttribute('data-template', el.textContent));

  function prepare() {
    const result = render();
    if (result.missing.length) {
      flagMissing(result.missing);
      return null;
    }
    const v = variant();
    const held = heldUntil(holdDays);
    const currency = cfg.currency || '';
    const lines = [cfg.product, `Size / build kit: ${v.title}, ${money(v.price)}`];
    result.lines.forEach((l) => lines.push(`${l.label}: ${l.value}`));
    result.upgrades.forEach((u) => lines.push(`Paid upgrade: ${u.title}, ${u.variant}, + ${money(u.price)}`));
    const values = {
      bike: `${cfg.product}, ${v.title}`,
      build: plain(lines.join('\n')),
      total: plain(money(total(result)), currency),
      held: `${held} (${holdDays} days, price only, in ${currency})`,
      page: `${window.location.origin}${cfg.productUrl}?variant=${v.id}`,
    };
    Object.entries(values).forEach(([key, value]) => {
      const field = sendForm.querySelector(`[data-bossi-field="${key}"]`);
      if (field) field.value = value;
    });
    fills.forEach((el) => {
      el.textContent = el
        .getAttribute('data-template')
        .replace(/\[date\]/g, held)
        .replace(/\[currency\]/g, currency)
        .replace(/\[days\]/g, String(holdDays))
        .replace(/\[bike\]/g, model);
    });
    return { held, currency, bike: model };
  }

  open.addEventListener('click', () => {
    if (!panel.hidden) {
      panel.hidden = true;
      open.setAttribute('aria-expanded', 'false');
      return;
    }
    if (!prepare()) return;
    panel.hidden = false;
    open.setAttribute('aria-expanded', 'true');
    panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    const first = panel.querySelector('input:not([type="hidden"])');
    try {
      first?.focus({ preventScroll: true });
    } catch (e) {
      /* ignore */
    }
  });

  sendForm.addEventListener('submit', (event) => {
    const first = sendForm.querySelector('[data-bossi-first]');
    const last = sendForm.querySelector('[data-bossi-last]');
    const full = sendForm.querySelector('[data-bossi-fullname]');
    if (full) full.value = [first?.value, last?.value].filter(Boolean).join(' ').trim();
    const info = prepare();
    if (!info) {
      event.preventDefault();
      return;
    }
    try {
      sessionStorage.setItem(
        'bossiBuildSent',
        JSON.stringify({ bike: info.bike, held: info.held, currency: info.currency, at: Date.now() })
      );
    } catch (e) {
      /* private browsing: the thanks page shows its normal message */
    }
  });

  // Shopify returns here with errors if the enquiry was rejected: reopen the panel.
  if (panel.querySelector('[data-bossi-send-errors]')) {
    panel.hidden = false;
    open.setAttribute('aria-expanded', 'true');
    try {
      sessionStorage.removeItem('bossiBuildSent');
    } catch (e) {
      /* ignore */
    }
  }
}

/* ---------- Pathway cards ---------- */

function initPaths(el) {
  if (el.dataset.bossiReady) return;
  el.dataset.bossiReady = '1';
  const cfg = readConfig(el, '[data-bossi-paths-config]');
  if (!cfg) return;
  const section = sectionOf(el);
  const money = makeMoney(cfg.moneySample);
  const cards = [...el.querySelectorAll('button[data-bossi-path]')];

  el.querySelectorAll('[data-bossi-path-price]').forEach((span) => {
    const cents = cfg.prices ? cfg.prices[span.dataset.bossiPathPrice] : null;
    span.textContent = cents === null || cents === undefined ? '' : `${cfg.from} ${money(cents)}`;
  });
  // Hide a card whose pathway this bike does not offer.
  cards.forEach((card) => {
    const key = card.dataset.bossiPath === 'frameset' ? 'frame' : 'complete';
    if (!cfg.prices || cfg.prices[key] === null || cfg.prices[key] === undefined) card.hidden = true;
  });

  const kitInputs = () =>
    [...section.querySelectorAll('input[type="radio"][data-option-name]')].filter(
      (input) => input.dataset.optionName.toLowerCase() === 'build kit'
    );
  const currentKit = () => (kitInputs().find((input) => input.checked)?.value || '').toLowerCase();

  function setPath(path, choose) {
    section.dataset.bossiPath = path;
    cards.forEach((card) => card.setAttribute('aria-pressed', String(card.dataset.bossiPath === path)));
    if (!choose) return;
    const inputs = kitInputs();
    if (path === 'frameset') {
      const frameset = inputs.find((input) => input.value.toLowerCase() === 'frameset');
      if (frameset && !frameset.checked) frameset.click();
    } else if (currentKit() === 'frameset') {
      const core =
        inputs.find((input) => input.value.toLowerCase() !== 'frameset' && input.dataset.optionAvailable !== 'false') ||
        inputs.find((input) => input.value.toLowerCase() !== 'frameset');
      core?.click();
    }
  }

  cards.forEach((card) => card.addEventListener('click', () => setPath(card.dataset.bossiPath, true)));
  setPath(currentKit() === 'frameset' ? 'frameset' : 'core', false);
  onVariantChange(section, () => {
    setTimeout(() => {
      const kit = currentKit();
      if (kit) setPath(kit === 'frameset' ? 'frameset' : 'core', false);
    }, 0);
  });
}

/* ---------- Phone bar ---------- */

function initBar(bar) {
  if (bar.dataset.bossiReady) return;
  bar.dataset.bossiReady = '1';
  const section = sectionOf(bar);
  const anchor = section.querySelector('product-form-component') || section.querySelector('[data-bossi-build]');
  if (!anchor || !('IntersectionObserver' in window)) return;
  bar.hidden = false;
  new IntersectionObserver(([entry]) => {
    bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
  }).observe(anchor);

  // Keep the price in step with the page's own price (which shows the build total).
  const priceEl = bar.querySelector('[data-bossi-bar-price]');
  const source = section.querySelector('product-price');
  if (priceEl && source) {
    const copy = () => {
      const bdi = source.querySelector('.price-item__group.price bdi') || source.querySelector('bdi');
      const text = bdi?.textContent.trim();
      if (text) priceEl.textContent = text;
    };
    new MutationObserver(copy).observe(source, { childList: true, subtree: true, characterData: true });
    copy();
  }
}

/* ---------- Start ---------- */

function boot(scope = document) {
  scope.querySelectorAll('[data-bossi-build]').forEach(initBuild);
  scope.querySelectorAll('[data-bossi-paths]').forEach(initPaths);
  scope.querySelectorAll('[data-bossi-bar]').forEach(initBar);
}

boot();
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) boot(event.target);
});
